// UI Manager - Valentine Itinerary Builder
import { gameState } from '../game/gameState';
import * as GAME_MESSAGES from '../data/messages';

import type { Quest } from '../data/quests';
import type { Achievement } from '../data/achievements';
import { ExperienceService } from '../services/ExperienceService';
import { Game } from './Game';
import { Audio } from './Audio';
import { UIAnimations } from './UIAnimations';
import gsap from 'gsap';

export class UI {
    private static instance: UI;

    private container: HTMLElement;
    private overlay!: HTMLDivElement;
    private header!: HTMLDivElement;
    private choiceContainer!: HTMLDivElement;
    private achievementQueue: Achievement[] = [];
    private isShowingAchievement: boolean = false;
    private currentPanel: HTMLElement | null = null;
    private isAnimating: boolean = false;

    private constructor(container: HTMLElement) {
        this.container = container;
        this.setupStyles();
        this.createOverlay();
        this.createMusicToggle();
        // this.createHeader(); // Created later
        // this.createChoiceContainer(); // Created later
        // this.createItineraryPanel(); // Created later

        // Initialize animation system
        UIAnimations.init(container);

        // Subscribe to game state
        gameState.subscribe((state) => this.onStateChange(state));

        // Check if we are in view-only mode (Shared Link)
        const state = gameState.getState();
        if (state.isViewOnly && state.publicToken) {
            this.showSharedView();
        } else {
            // Attempt auto-restore for returning users
            this.tryRestoreSession();
        }
    }

    private async tryRestoreSession(): Promise<void> {
        if (ExperienceService.getStoredSessionId()) {
            try {
                await gameState.initSession();
                if (gameState.isGameComplete()) {
                    this.showCompletedView();
                    return;
                }
            } catch (e) {
                console.warn('Failed to restore session', e);
            }
        }
        // Fallback to intro
        setTimeout(() => this.showIntroScreen(), 500);
    }

    private resetGame(): void {
        ExperienceService.clearSession();
        window.location.reload();
    }

    static init(container: HTMLElement): UI {
        if (!UI.instance) {
            UI.instance = new UI(container);
        }
        return UI.instance;
    }

    static getInstance(): UI {
        return UI.instance;
    }

    private setupStyles(): void {
        const style = document.createElement('style');
        style.textContent = `
            @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&display=swap');
            
            .ui-overlay {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 100;
                font-family: 'Outfit', sans-serif;
            }

            .dim-overlay {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0,0,0,0.5);
                opacity: 0;
                pointer-events: auto;
                z-index: 90;
                backdrop-filter: blur(2px);
            }

            /* Minimal Header */
            .ui-header {
                position: absolute;
                top: 10px;
                left: 10px;
                right: 10px;
                display: flex;
                align-items: center;
                justify-content: space-between;
                pointer-events: auto;
                opacity: 0;
                transform: translateY(-20px);
            }
            
            .ui-header.visible {
                opacity: 1;
                transform: translateY(0);
            }
            
            .ui-title {
                background: rgba(255,255,255,0.9);
                padding: 8px 16px;
                border-radius: 20px;
                font-size: 14px;
                font-weight: 700;
                color: #ff6b9d;
                display: flex;
                align-items: center;
                gap: 8px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            
            /* Itinerary Panel - Side Panel style */
            .itinerary-panel {
                position: absolute;
                right: 20px;
                top: 80px;
                width: 300px;
                background: rgba(255, 255, 255, 0.95);
                border-radius: 16px;
                box-shadow: 0 8px 30px rgba(0,0,0,0.1);
                border: 2px solid #ffebf0;
                padding: 20px;
                pointer-events: auto;
                transform: translateX(20px);
                opacity: 0;
                max-height: 70vh;
                display: flex;
                flex-direction: column;
            }

            .itinerary-panel.visible {
                transform: translateX(0);
                opacity: 1;
            }

            .itinerary-title {
                font-size: 18px;
                font-weight: 700;
                color: #ff6b9d;
                margin-bottom: 15px;
                text-align: center;
                border-bottom: 2px solid #fff0f5;
                padding-bottom: 10px;
            }

            .itinerary-list {
                list-style: none;
                padding: 0;
                margin: 0;
                overflow-y: auto;
                flex-grow: 1;
                min-height: 0;
            }

            .itinerary-item {
                display: flex;
                flex-direction: column;
                gap: 4px;
                padding: 10px;
                border-radius: 12px;
                margin-bottom: 8px;
                background: #fff5f8;
                border: 1px solid #ffe0eb;
                font-size: 14px;
                color: #555;
            }

            .itinerary-question {
                font-size: 11px;
                color: #ff6b9d;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                opacity: 0.8;
                margin-bottom: 2px;
            }

            .itinerary-answer-row {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .itinerary-icon {
                font-size: 18px;
            }
            
            /* Floating Choice Container - Bottom Center Horizontal */
            .choice-container {
                position: absolute;
                bottom: 50px;
                left: 50%;
                transform: translateX(-50%);
                display: flex;
                flex-direction: row;
                gap: 20px;
                pointer-events: auto;
                opacity: 0;
                align-items: center;
                justify-content: center;
                z-index: 105;
                width: 100%;
                max-width: 800px;
                flex-wrap: wrap; /* Allow wrapping on small screens */
            }
            
            .choice-container.visible {
                opacity: 1;
            }
            
            /* Choice Chips */
            .choice-chip {
                padding: 16px 32px;
                border-radius: 50px; /* Pill shape looks better horizontally */
                border: 2px solid rgba(255,255,255,0.8);
                background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,240,245,0.95) 100%);
                backdrop-filter: blur(10px);
                font-family: 'Outfit', sans-serif;
                font-size: 16px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                gap: 12px;
                box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
                color: #333;
                min-width: 200px; /* Fixed min width for uniformity */
                justify-content: center; /* Center text */
                text-align: center;
            }
            
            .choice-chip:hover {
                border-color: #ff6b9d;
                transform: translateX(-5px);
                box-shadow: 0 6px 20px rgba(255, 107, 157, 0.25);
            }
            
            .choice-chip.selected {
                background: linear-gradient(135deg, #ff6b9d 0%, #ff8fab 100%);
                color: white;
                border-color: #ff6b9d;
            }
            
            .choice-chip-icon {
                font-size: 22px;
            }

            /* Buttons */
            .game-button {
                padding: 12px 28px;
                border-radius: 25px;
                border: none;
                font-family: 'Outfit', sans-serif;
                font-size: 14px;
                font-weight: 700;
                cursor: pointer;
                transition: all 0.15s ease;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }
            
            .game-button.primary {
                background: linear-gradient(135deg, #ff6b9d 0%, #ff8fab 100%);
                color: white;
                box-shadow: 0 4px 15px rgba(255, 107, 157, 0.4);
            }
            
            .game-button.primary:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(255, 107, 157, 0.5);
            }

            .music-toggle {
                position: absolute;
                bottom: 20px;
                left: 20px;
                width: 40px;
                height: 40px;
                background: white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 20px;
                box-shadow: 0 4px 10px rgba(0,0,0,0.1);
                border: 2px solid #fff0f5;
                cursor: pointer;
                z-index: 1000;
                pointer-events: auto;
                transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
            }

            .music-toggle:hover {
                transform: scale(1.1);
                box-shadow: 0 6px 15px rgba(255, 107, 157, 0.2);
            }

            .music-toggle.muted {
                opacity: 0.6;
                filter: grayscale(1);
            }
            
            /* Summary Screen */
            .summary-screen {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 90%;
                max-width: 450px;
                background: linear-gradient(180deg, #fff5f8 0%, white 100%);
                border-radius: 20px;
                padding: 35px;
                text-align: center;
                box-shadow: 0 20px 50px rgba(255, 107, 157, 0.3);
                pointer-events: auto;
                z-index: 95;
            }
            
            .summary-title {
                font-size: 24px;
                font-weight: 700;
                color: #ff6b9d;
                margin-bottom: 12px;
            }

            /* Achievement Toast */
            .achievement-toast {
                position: absolute;
                top: 80px;
                left: 20px; /* Left side now */
                background: linear-gradient(135deg, #ffd700 0%, #ffec8b 100%);
                border-radius: 14px;
                padding: 12px 18px;
                display: flex;
                align-items: center;
                gap: 12px;
                box-shadow: 0 8px 30px rgba(255, 215, 0, 0.4);
                pointer-events: auto;
                transform: translateX(-120%);
                z-index: 200;
            }
            
            .achievement-toast.visible {
                transform: translateX(0);
            }
            
            .achievement-icon {
                font-size: 28px;
            }
             
             .achievement-info {
                display: flex;
                flex-direction: column;
            }
            
            .achievement-label {
                font-size: 10px;
                color: #8B7355;
                text-transform: uppercase;
                letter-spacing: 1px;
                font-weight: 600;
            }
            
            .achievement-title {
                font-size: 14px;
                font-weight: 700;
                color: #5D4037;
            }
            .mobile-notice {
                display: none;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                background: rgba(255, 255, 255, 0.9);
                color: #ff6b9d;
                padding: 8px;
                text-align: center;
                font-size: 12px;
                font-weight: 600;
                z-index: 200;
                backdrop-filter: blur(4px);
                border-bottom: 1px solid rgba(255, 107, 157, 0.2);
            }

            /* Mobile Responsiveness */
            @media (max-width: 768px) {
                .mobile-notice {
                    display: block;
                }
                .intro-title {
                    font-size: 36px !important;
                }
                
                .intro-subtitle {
                    font-size: 14px !important;
                }

                .choice-container {
                    bottom: 20px;
                    width: 95%;
                    gap: 10px;
                }

                .choice-chip {
                    padding: 10px 20px;
                    min-width: 140px;
                    font-size: 14px;
                }

                .choice-chip-icon {
                    font-size: 18px;
                }

                .itinerary-panel {
                    top: auto;
                    bottom: 150px; /* Above choices */
                    right: 10px;
                    left: auto;
                    width: 60%;
                    max-height: 50vh;
                    transform: translateY(20px);
                }
                
                .itinerary-panel.visible {
                    transform: translateY(0);
                }
                
                .summary-screen {
                    width: 95%;
                    padding: 20px;
                    top: 15%; /* Move to top instead of center */
                    transform: translateX(-50%); /* Remove Y centering */
                }
                
                .summary-title {
                    font-size: 20px;
                }
            }
        `;
        document.head.appendChild(style);
    }

    private createOverlay(): void {
        this.overlay = document.createElement('div');
        this.overlay.className = 'ui-overlay';
        this.container.appendChild(this.overlay);
        this.createMobileNotice();
    }

    private createMobileNotice(): void {
        const notice = document.createElement('div');
        notice.className = 'mobile-notice';
        notice.innerHTML = '📱 Best viewed on larger screens';
        this.overlay.appendChild(notice);
    }



    private createMusicToggle(): void {
        const toggle = document.createElement('div');
        toggle.className = 'music-toggle';
        toggle.innerHTML = '🎵';
        toggle.title = 'Toggle Music';

        // Start playing on first interaction
        const startAudio = () => {
            Audio.getInstance().playBGM();
            // Remove this listener after first interaction
            document.removeEventListener('click', startAudio);
            document.removeEventListener('touchstart', startAudio);
            document.removeEventListener('keydown', startAudio);
        };

        document.addEventListener('click', startAudio);
        document.addEventListener('touchstart', startAudio);
        document.addEventListener('keydown', startAudio);

        toggle.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent re-triggering startAudio if clicking toggle
            const isMuted = Audio.getInstance().toggleMusic();
            if (isMuted) {
                toggle.classList.add('muted');
                toggle.innerHTML = '🔇';
            } else {
                toggle.classList.remove('muted');
                toggle.innerHTML = '🎵';
            }
        });

        this.overlay.appendChild(toggle);
    }

    private createHeader(): void {
        this.header = document.createElement('div');
        this.header.className = 'ui-header';
        // User requested removing the pill
        this.header.innerHTML = ``;
        /*
        this.header.innerHTML = `
            <div class="ui-title">
                💕 Itinerary Builder
            </div>
        `;
        */
        this.overlay.appendChild(this.header);

        // Use timeout to allow styles to apply
        setTimeout(() => {
            if (this.header) this.header.classList.add('visible');
        }, 100);
    }

    private createChoiceContainer(): void {
        this.choiceContainer = document.createElement('div');
        this.choiceContainer.className = 'choice-container';
        this.overlay.appendChild(this.choiceContainer);
    }

    private createItineraryPanel(): void {
        const panel = document.createElement('div');
        panel.className = 'itinerary-panel';
        panel.id = 'itinerary-panel';
        panel.innerHTML = `
            <div class="itinerary-title">Your Plan 💌</div>
            <ul class="itinerary-list" id="itinerary-list">
                <!-- Items added dynamically -->
            </ul>
        `;
        this.overlay.appendChild(panel);

        // Animate in
        gsap.to(panel, {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power2.out',
            delay: 0.5,
            onStart: () => panel.classList.add('visible'),
            onComplete: () => this.populateItineraryFromAnswers()
        });
    }

    private populateItineraryFromAnswers(): void {
        const state = gameState.getState();
        const answers = state.answers;

        if (!answers || Object.keys(answers).length === 0) return;

        // Import QUESTS dynamically to get question text
        import('../data/quests').then(({ QUESTS }) => {
            // Add each answered question to the itinerary
            for (const [questKey, answerValue] of Object.entries(answers)) {
                const quest = QUESTS.find(q => q.key === questKey);
                if (!quest) continue;

                // Find the selected option to get the label and icon
                const selectedOption = quest.options.find(o => o.value === answerValue);
                if (!selectedOption) continue;

                const label = selectedOption.label;
                const icon = selectedOption.icon || '✨';
                const questionText = quest.title;

                this.addToItineraryWithoutAnimation(label, icon, questionText);
            }
        });
    }

    private addToItineraryWithoutAnimation(label: string, icon: string, questionText: string): void {
        const list = document.getElementById('itinerary-list');
        if (!list) return;

        const item = document.createElement('li');
        item.className = 'itinerary-item';
        item.innerHTML = `
            <div class="itinerary-question">${questionText}</div>
            <div class="itinerary-answer-row">
                <span class="itinerary-icon">${icon}</span>
                <span class="itinerary-text">${label}</span>
            </div>
        `;
        list.appendChild(item);
    }

    private addToItinerary(label: string, icon: string, questionText: string): void {
        const list = document.getElementById('itinerary-list');
        if (!list) return;

        const item = document.createElement('li');
        item.className = 'itinerary-item';
        item.innerHTML = `
            <div class="itinerary-question">${questionText}</div>
            <div class="itinerary-answer-row">
                <span class="itinerary-icon">${icon}</span>
                <span class="itinerary-text">${label}</span>
            </div>
        `;

        item.style.opacity = '0';
        item.style.transform = 'translateX(20px)';
        list.appendChild(item);

        gsap.to(item, { opacity: 1, x: 0, duration: 0.5, ease: 'back.out(1.7)' });

        // Scroll to bottom
        setTimeout(() => {
            list.scrollTo({
                top: list.scrollHeight,
                behavior: 'smooth'
            });
        }, 100);
    }

    private async clearCurrentPanel(): Promise<void> {
        return new Promise((resolve) => {
            if (this.currentPanel) {
                gsap.to(this.currentPanel, {
                    opacity: 0,
                    scale: 0.9,
                    duration: 0.2,
                    onComplete: () => {
                        this.currentPanel?.remove();
                        this.currentPanel = null;
                        resolve();
                    }
                });
            } else {
                resolve();
            }
        });
    }

    showIntroScreen(): void {
        // Clear anything existing
        if (this.currentPanel) {
            this.currentPanel.remove();
            this.currentPanel = null;
        }

        // Get custom name if available
        const state = gameState.getState();
        const partnerName = state.experienceConfig?.partnerName || 'My Valentine';

        // Minimal Title Overlay
        const overlay = document.createElement('div');
        overlay.className = 'intro-overlay';
        overlay.innerHTML = `
            <div class="intro-content">
                <div class="intro-subtitle">A Valentine's Surprise</div>
                <div class="intro-title">For ${partnerName}</div>
                <button class="game-button primary glass-button" id="btn-open-heart">
                    Open My Heart 💖
                </button>
            </div>
        `;

        // Add specific styles for this overlay if not present
        if (!document.getElementById('intro-styles')) {
            const s = document.createElement('style');
            s.id = 'intro-styles';
            s.textContent = `
                .intro-overlay {
                    position: absolute;
                    bottom: 15%;
                    left: 0;
                    width: 100%;
                    text-align: center;
                    pointer-events: auto;
                    z-index: 50;
                    display: flex;
                    justify-content: center;
                }
                .intro-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 15px;
                    animation: float 3s ease-in-out infinite;
                }
                .intro-subtitle {
                    font-size: 18px;
                    color: #555;
                    font-weight: 500;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    text-shadow: 0 2px 10px rgba(255,255,255,0.8);
                }
                .intro-title {
                    font-size: 60px;
                    font-weight: 700;
                    color: #FF6B9D;
                    font-family: 'Outfit', sans-serif;
                    text-shadow: 0 4px 20px rgba(255,107,157, 0.3);
                    margin-bottom: 20px;
                }
                .glass-button {
                    background: rgba(255, 255, 255, 0.2) !important;
                    backdrop-filter: blur(10px);
                    border: 2px solid rgba(255, 255, 255, 0.5) !important;
                    color: #FF6B9D !important;
                    font-size: 18px !important;
                    padding: 15px 40px !important;
                    transition: all 0.3s ease !important;
                }
                .glass-button:hover {
                    background: rgba(255, 255, 255, 0.4) !important;
                    transform: scale(1.05);
                    box-shadow: 0 0 30px rgba(255, 107, 157, 0.4);
                }
                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
            `;
            document.head.appendChild(s);
        }

        this.overlay.appendChild(overlay);
        this.currentPanel = overlay;

        // Button handler
        document.getElementById('btn-open-heart')?.addEventListener('click', () => this.onOpenHeart());
    }

    async onOpenHeart(): Promise<void> {
        if (this.isAnimating) return;
        this.isAnimating = true;

        try { Audio.getInstance().play('success'); } catch (e) { /* Audio not ready */ }

        // 1. Remove UI
        await this.clearCurrentPanel();

        // 2. Explode Heart
        const game = Game.getInstance();
        await game.world.heart.explode();

        // 3. Show Letter (The "Note")
        this.showLetterOverlay();
    }

    private showLetterOverlay(): void {
        const state = gameState.getState();
        const partnerName = state.experienceConfig?.partnerName || 'My Valentine';
        // Use custom message if available, else default
        const rawMessage = state.experienceConfig?.valMessage || GAME_MESSAGES.LETTER_MESSAGE;
        const message = rawMessage.replace('{partner}', partnerName);

        const overlay = document.createElement('div');
        overlay.className = 'letter-overlay-container';
        overlay.innerHTML = `
            <div class="letter-card">
                <div class="letter-text">
                    ${message.replace(/\n/g, '<br>')}
                </div>
                <button class="game-button primary" id="btn-start-game">
                    Yes! Let's Build Our World 💖
                </button>
            </div>
        `;

        // Add styles if not present
        if (!document.getElementById('letter-styles')) {
            const s = document.createElement('style');
            s.id = 'letter-styles';
            s.textContent = `
                .letter-overlay-container {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background: rgba(255, 255, 255, 0.4); /* Subtle backing */
                    backdrop-filter: blur(4px);
                    z-index: 60;
                    pointer-events: auto;
                }
                .letter-card {
                    background: white;
                    padding: 40px;
                    border-radius: 20px;
                    box-shadow: 0 20px 60px rgba(255, 107, 157, 0.25);
                    max-width: 500px;
                    text-align: center;
                    border: 2px solid #fff0f5;
                    transform: scale(0.9);
                    opacity: 0;
                }
                .letter-text {
                    font-size: 16px;
                    line-height: 1.8;
                    color: #555;
                    margin-bottom: 30px;
                    font-family: 'Outfit', sans-serif;
                }
                /* Highlight the "Will you be my Valentine" part */
                .letter-text br:last-of-type + textNode, /* Trickier to target text directly with regex replace above */
                .letter-text strong {
                    color: #ff6b9d;
                    font-weight: 700;
                    font-size: 18px;
                }
            `;
            document.head.appendChild(s);
        }

        this.overlay.appendChild(overlay);
        this.currentPanel = overlay;

        // Animate In
        const card = overlay.querySelector('.letter-card');
        gsap.to(card, {
            scale: 1,
            opacity: 1,
            duration: 0.8,
            ease: 'back.out(1.5)'
        });

        document.getElementById('btn-start-game')?.addEventListener('click', () => this.onLetterConfirmed());
    }

    async onLetterConfirmed(): Promise<void> {
        if (this.currentPanel) {
            await this.clearCurrentPanel();
        }

        const game = Game.getInstance();

        // 4. Initialize Session & Transition View
        try {
            await gameState.initSession();
            await gameState.answerQuest('valentine', 'yes'); // Implicit "Yes"
        } catch (e) { console.error(e); }

        // Transition Camera to Character
        const assistantPos = game.world.assistant.getPosition();
        await game.view.transitionFromIntro(assistantPos.x, assistantPos.z);

        // 5. Start Game
        this.showGameScreen();
        this.isAnimating = false;
    }

    showGameScreen(): void {
        // Initialize Game UI elements
        this.createHeader();
        this.createChoiceContainer();
        this.createItineraryPanel();

        // Start the game loop
        this.startCurrentQuest();
    }

    private async startCurrentQuest(): Promise<void> {
        const quest = gameState.getCurrentQuest();
        if (!quest) {
            // No current quest, check if game over or just starting
            this.showNextQuest();
            return;
        }
        await this.showQuestGuided(quest);
    }

    private async showSharedView(): Promise<void> {
        try {
            // Wait a moment for world to be ready
            await new Promise(resolve => setTimeout(resolve, 500));

            // 1. Setup Game Screen elements
            this.createHeader();
            this.createItineraryPanel();

            // 2. Populate Itinerary from State
            await this.populateItineraryFromState();

            // 3. Move Camera to nice view
            const game = Game.getInstance();
            if (game.world && game.world.assistant) {
                const assistant = game.world.assistant;
                const pos = assistant.getPosition();
                await game.view.transitionToFrontView(pos.x, pos.z, 5.0);
                assistant.lookAtCamera();
                assistant.startIdleAnimation();
            } else {
                console.warn('⚠️ World or Assistant not ready yet');
            }

            // 4. Show Summary Screen (ReadOnly version)
            this.showSummaryScreen(true);
        } catch (error) {
            console.error('❌ Error in showSharedView:', error);
            // Fallback to avoid blank screen
            this.showIntroScreen();
        }
    }

    private async populateItineraryFromState(): Promise<void> {
        const state = gameState.getState();
        const answers = state.answers;
        const { QUESTS } = await import('../data/quests');

        QUESTS.forEach(quest => {
            const answerKeys = Object.keys(answers);
            if (answerKeys.includes(quest.key)) {
                const val = answers[quest.key];

                // Handle multi-select values (comma separated)
                const values = val.split(',');
                const selectedOptions = quest.options?.filter(o => values.includes(o.value)) || [];

                if (selectedOptions.length > 0) {
                    // Combine labels and icons
                    const combinedLabel = selectedOptions.map(o => o.label).join(' + ');
                    const combinedIcon = selectedOptions.map(o => o.icon || '✨').join('');
                    const questionText = quest.title;

                    this.addToItinerary(combinedLabel, combinedIcon, questionText);
                }
            }
        });
    }

    private async showCompletedView(): Promise<void> {
        try {
            await new Promise(resolve => setTimeout(resolve, 500));

            this.createHeader();
            this.createItineraryPanel();

            await this.populateItineraryFromState();

            const game = Game.getInstance();
            if (game.world && game.world.assistant) {
                const assistant = game.world.assistant;
                const pos = assistant.getPosition();
                await game.view.transitionToFrontView(pos.x, pos.z, 5.0);
                assistant.lookAtCamera();
                assistant.startIdleAnimation();
            }

            this.showSummaryScreen(false, true);
        } catch (error) {
            console.error('Error showing completed view:', error);
            this.showIntroScreen();
        }
    }

    async showNextQuest(): Promise<void> {
        const quest = gameState.getCurrentQuest();

        if (!quest) {
            if (gameState.isGameComplete()) {
                this.showSummaryScreen();
            } else {
                // Try to advance if somehow stuck? 
                // Or maybe we just finished a chapter
                const nextChapter = gameState.getNextChapterIfReady();
                if (nextChapter) {
                    // Auto-advance for now in this simple version
                    gameState.advanceChapter();
                    setTimeout(() => this.showNextQuest(), 500);
                }
            }
            this.hideChoices();
            return;
        }

        await this.showQuestGuided(quest);
    }

    async showQuestGuided(quest: Quest): Promise<void> {
        const game = Game.getInstance();
        const view = game.view;
        const assistant = game.world.assistant;

        // 1. Camera focuses on assistant (Front View)
        const assistantPos = assistant.getPosition();
        // Offset target X to shift character to the left, since list is on the right
        await view.transitionToFrontView(assistantPos.x, assistantPos.z, 4.0);

        // Make assistant look at camera (approximate front position)
        if (assistant.group) {
            assistant.lookAtCamera();
        }

        // Assistant idle/point
        assistant.startIdleAnimation();

        // 2. Show speech bubble with the quest message
        // Include the actual question in the bubble so the user knows what to answer
        const message = `<strong>${quest.title}</strong><br/>${quest.assistantMessage}`;
        assistant.showSpeechBubble(message);

        // 3. Show choice chips
        await this.showChoices(quest);
    }

    private showChoices(quest: Quest): Promise<void> {
        return new Promise((resolve) => {
            this.choiceContainer.innerHTML = '';

            // Selected values for multi-select
            const selectedValues = new Set<string>();

            if (!quest.options || quest.options.length === 0) {
                resolve();
                return;
            }

            quest.options.forEach((opt, i) => {
                const chip = document.createElement('button');
                chip.className = 'choice-chip';
                chip.dataset.value = opt.value;
                chip.innerHTML = `
                    <span class="choice-chip-icon">${opt.icon || '💝'}</span>
                    <span>${opt.label}</span>
                `;
                chip.style.opacity = '0';
                chip.style.transform = 'translateY(50px)'; // Slide up from further down
                this.choiceContainer.appendChild(chip);

                // Stagger animation
                gsap.to(chip, {
                    opacity: 1,
                    y: 0,
                    duration: 0.4,
                    delay: 0.2 + i * 0.1,
                    ease: 'back.out(1.7)',
                });

                // Click handler
                chip.addEventListener('click', () => {
                    if (quest.multiSelect) {
                        // Toggle selection
                        if (selectedValues.has(opt.value)) {
                            selectedValues.delete(opt.value);
                            chip.classList.remove('selected');
                        } else {
                            selectedValues.add(opt.value);
                            chip.classList.add('selected');
                        }

                        // Update Done button state
                        const doneBtn = this.choiceContainer.querySelector('.done-button') as HTMLButtonElement;
                        if (doneBtn) {
                            doneBtn.disabled = selectedValues.size === 0;
                            doneBtn.style.opacity = selectedValues.size > 0 ? '1' : '0.5';
                        }
                    } else {
                        // Single select - immediate answer
                        this.onQuestAnswer(quest.key, opt.value, chip);
                    }
                });

                // Hover effects
                chip.addEventListener('mouseenter', () => UIAnimations.buttonHover(chip));
                chip.addEventListener('mouseleave', () => UIAnimations.buttonUnhover(chip));
            });

            // Add Done button for multi-select
            if (quest.multiSelect) {
                const doneBtn = document.createElement('button');
                doneBtn.className = 'game-button primary done-button';
                doneBtn.innerText = 'Done ✅';
                doneBtn.style.marginTop = '15px'; // Separate from chips
                doneBtn.style.opacity = '0'; // Start hidden/disabled look
                doneBtn.disabled = true;

                this.choiceContainer.appendChild(doneBtn);

                // Animate in last
                gsap.to(doneBtn, {
                    opacity: 0.5, // Start disabled opacity
                    y: 0,
                    duration: 0.4,
                    delay: 0.2 + quest.options.length * 0.1,
                    ease: 'back.out(1.7)'
                });

                doneBtn.addEventListener('click', () => {
                    if (selectedValues.size === 0) return;

                    // Combine values
                    const values = Array.from(selectedValues);
                    const combinedValue = values.join(',');

                    // Combine labels and icons for itinerary
                    const selectedOptions = quest.options?.filter(o => selectedValues.has(o.value)) || [];
                    const combinedLabel = selectedOptions.map(o => o.label).join(' + ');
                    const combinedIcon = selectedOptions.map(o => o.icon || '✨').join('');

                    this.onQuestAnswer(quest.key, combinedValue, doneBtn, combinedLabel, combinedIcon);
                });
            }

            this.choiceContainer.classList.add('visible');
            gsap.to(this.choiceContainer, { opacity: 1, duration: 0.3 });

            resolve();
        });
    }

    async onQuestAnswer(questKey: string, value: string, selectedChip: HTMLElement, customLabel?: string, customIcon?: string): Promise<void> {
        if (this.isAnimating) return;
        this.isAnimating = true;

        try { Audio.getInstance().play('success'); } catch (e) { /* Audio not ready */ }

        // Visual feedback
        selectedChip.classList.add('selected');

        // Confetti from selection
        const rect = selectedChip.getBoundingClientRect();
        UIAnimations.createConfetti(rect.left + rect.width / 2, rect.top, 25);

        // Update Itinerary Panel
        // Use custom label/icon if provided (multi-select), otherwise extract from chip
        let selectedOption = customLabel;
        let selectedIcon = customIcon;

        if (!selectedOption) {
            selectedOption = selectedChip.querySelector('span:nth-child(2)')?.textContent || value;
        }
        if (!selectedIcon) {
            selectedIcon = selectedChip.querySelector('.choice-chip-icon')?.textContent || '✨';
        }

        // Find quest title for context
        const { getQuestByKey } = await import('../data/quests');
        const quest = getQuestByKey(questKey);
        const questionText = quest?.title || 'Question';

        this.addToItinerary(selectedOption!, selectedIcon!, questionText);

        // Fade out other chips (if single select behavior essentially)
        // For multi-select, we just fade everything out
        const allChips = this.choiceContainer.querySelectorAll('.choice-chip');
        allChips.forEach((chip) => {
            if (chip !== selectedChip) {
                gsap.to(chip, { opacity: 0.3, scale: 0.95, duration: 0.2 });
            }
        });

        // Hide choices
        await new Promise(resolve => setTimeout(resolve, 800));
        this.hideChoices();

        // Get game components
        const game = Game.getInstance();
        const assistant = game.world.assistant;

        // Hide speech bubble
        assistant.hideSpeechBubble();

        try {
            const { newAchievements } = await gameState.answerQuest(questKey, value);

            // Queue achievements
            if (newAchievements && newAchievements.length > 0) {
                this.achievementQueue.push(...newAchievements);
                this.processAchievementQueue();
            }

            // Wait a bit
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Next quest
            this.showNextQuest();
        } catch (error) {
            console.error('Failed to answer quest:', error);
        }

        this.isAnimating = false;
    }

    showSummaryScreen(isReadOnly: boolean = false, canStartOver: boolean = false): void {
        const screen = document.createElement('div');
        screen.className = 'summary-screen';

        let title = isReadOnly ? "💌 A Valentine Plan For You!" : "💕 Your Valentine Itinerary is Ready!";
        let subtitle = isReadOnly ? "Someone special created this for you." : "Review your perfect day together.";

        let buttonsHtml = '';

        if (!isReadOnly) {
            // Show for both returning users AND newly completed games
            if (canStartOver) {
                title = "Welcome Back! 💌";
                subtitle = "Here is the plan you created.";
            }

            buttonsHtml = `
                <div style="display: flex; flex-direction: column; gap: 12px; align-items: center; margin-top: 10px; width: 100%;">
                    <button class="game-button primary" id="btn-share" style="width: 100%;">Share with Valentine 💝</button>
                    <button class="game-button" id="btn-restart" style="background: white; border: 2px solid #ffccd5; color: #ff6b9d; font-size: 13px; padding: 10px 20px; min-width: 150px;">Start All Over 🔄</button>
                </div>
            `;
        } else {
            // Read Only view (Shared Link)
            const buttonText = "Create Your Own 💖";
            buttonsHtml = `<button class="game-button primary" id="btn-action">${buttonText}</button>`;
        }

        screen.innerHTML = `
            <div class="summary-title">${title}</div>
            <p style="color: #666; margin-bottom: 20px;">${subtitle}</p>
            ${buttonsHtml}
        `;
        this.overlay.appendChild(screen);
        this.currentPanel = screen;

        gsap.from(screen, { scale: 0.8, opacity: 0, duration: 0.5, ease: 'back.out(1.7)' });

        UIAnimations.createConfetti(window.innerWidth / 2, window.innerHeight / 2, 60);

        if (!isReadOnly) {
            document.getElementById('btn-share')?.addEventListener('click', () => this.shareGame());
            document.getElementById('btn-restart')?.addEventListener('click', () => this.resetGame());
        } else {
            const btn = document.getElementById('btn-action');
            if (isReadOnly) {
                btn?.addEventListener('click', () => {
                    window.location.href = 'https://cupidplan.com';
                });
            }
        }
    }


    async shareGame(): Promise<void> {
        const state = gameState.getState();
        if (state.publicToken) {
            const success = await ExperienceService.copyShareUrl(state.publicToken);
            if (success) {
                UIAnimations.createConfetti(window.innerWidth / 2, window.innerHeight / 2, 30);
                this.showToast('Link copied to clipboard! 💌');


                // Notify creator about sharing if experienceId is available
                const expId = state.experienceConfig?.id;
                if (expId && state.publicToken) {
                    ExperienceService.notifySharing(expId, state.publicToken);
                }
            } else {
                this.showToast('Failed to copy link. 😢');
            }
        }
    }

    private showToast(message: string): void {
        const toast = document.createElement('div');
        toast.className = 'achievement-toast'; // Re-use the nice toast style
        toast.innerHTML = `
            <div class="achievement-icon">📋</div>
            <div class="achievement-info">
                <span class="achievement-label">Notification</span>
                <span class="achievement-title">${message}</span>
            </div>
        `;

        this.overlay.appendChild(toast);

        // Animate in
        gsap.to(toast, {
            x: 0,
            duration: 0.5,
            ease: 'back.out(1.2)',
            onStart: () => toast.classList.add('visible')
        });

        // Animate out
        setTimeout(() => {
            gsap.to(toast, {
                x: -100,
                opacity: 0,
                duration: 0.5,
                onComplete: () => { toast.remove(); }
            });
        }, 3000);
    }

    private processAchievementQueue(): void {
        if (this.isShowingAchievement || this.achievementQueue.length === 0) return;

        const achievement = this.achievementQueue.shift()!;
        this.showAchievement(achievement);
    }

    private showAchievement(achievement: Achievement): void {
        this.isShowingAchievement = true;

        try { Audio.getInstance().play('achievement'); } catch (e) { /* Audio not ready */ }

        const toast = document.createElement('div');
        toast.className = 'achievement-toast';
        toast.innerHTML = `
            <div class="achievement-icon">${achievement.icon}</div>
            <div class="achievement-info">
                <span class="achievement-label">Achievement Unlocked</span>
                <span class="achievement-title">${achievement.title}</span>
            </div>
        `;

        this.overlay.appendChild(toast);

        // Animate in
        gsap.to(toast, {
            x: 0,
            duration: 0.5,
            ease: 'back.out(1.2)',
            onStart: () => toast.classList.add('visible')
        });

        // Animate out
        setTimeout(() => {
            gsap.to(toast, {
                x: -100,
                opacity: 0,
                duration: 0.5,
                onComplete: () => {
                    toast.remove();
                    this.isShowingAchievement = false;
                    this.processAchievementQueue();
                }
            });
        }, 3000);
    }

    private hideChoices(): void {
        gsap.to(this.choiceContainer, {
            opacity: 0,
            y: 20,
            duration: 0.3,
            onComplete: () => {
                this.choiceContainer.classList.remove('visible');
                this.choiceContainer.innerHTML = '';
            }
        });
    }

    // State update handler
    private onStateChange(_state: any): void {
        // Can be used to update other UI elements if needed
    }
}
